import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../../components/Animations'
import { ButtonLeadingIcon, PrimaryButton, SecondaryButton } from '../../components/Button'
import CustomerForm from '../../components/CustomerForm'
import { DashboardPage, EmptyState, InlineNotice, PageActionRow, PageHeaderRow } from '../../components/DashboardPrimitives'
import Gate from '../../components/Gate'
import LoadingBar from '../../components/LoadingBar'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import Pagination from '../../components/Pagination'
import Radom, { Customer, ExternalAccount, ExternalAccountWithContact, Payout, WithdrawalAccount } from '../../state/Radom'
import User from '../../state/User'
import { networkName } from '../../util/AssetLabels'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../util/ExternalAccounts'
import { getMethod, prodNetworks, testNetworks } from '../../util/Managed'
import { useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import { StatusBadge } from '../CheckoutSessions'
import RecipientAvatar from '../Send/RecipientAvatar'
import { focusRing } from '../Send/SendStyles'
import CreateExternalAccountModal from './CreateExternalAccountModal'

/**
 * The payout address book: the people and businesses this organization pays
 * and is paid by. Built around one job: find who you want to pay, see how you
 * can pay them, and send. The organization's own accounts are not contacts;
 * they live on Send funds as destinations and on the Your accounts page.
 */

const PAGE_SIZE = 25
const RECENT_LIMIT = 8
const FALLBACK_ACCOUNT_LOOKUPS = 50

type ContactFilter = 'all' | 'payable' | 'payers'

const SearchBar = styled.label`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 14px;
  color: var(--text-faint, #8a919e);
  display: flex;
  gap: 12px;
  min-height: 52px;
  padding: 0 16px;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;

  &:focus-within {
    border-color: var(--focus-border, rgba(17, 17, 17, 0.22));
    box-shadow: 0 0 0 3px var(--focus-ring, rgba(17, 17, 17, 0.08));
  }

  input {
    background: transparent;
    border: 0;
    color: var(--ink-strong, #121722);
    flex: 1;
    font-family: var(--font-sans);
    font-size: 15px;
    min-width: 0;
    outline: 0;

    &::placeholder {
      color: var(--text-faint, #8a919e);
    }
  }
`

const SearchGlyph = styled.svg`
  flex: 0 0 auto;
  height: 18px;
  width: 18px;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  min-height: 30px;
  padding: 0 4px;
`

const SectionLabel = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

const RecentStrip = styled.div`
  display: flex;
  gap: 4px;
  margin: 0 -6px;
  overflow-x: auto;
  padding: 0 6px 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const RecentPerson = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 14px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  font-family: var(--font-sans);
  gap: 8px;
  padding: 8px 6px 6px;
  transition: background-color 0.16s ease, transform 0.16s ease;
  width: 104px;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  ${focusRing}

  > span {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.25;
    max-width: 100%;
    min-height: 30px;
    overflow: hidden;
    text-align: center;
    word-break: break-word;
  }
`

const RecentSkeleton = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 4px;
  padding: 8px 0 4px;

  > div {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 104px;
  }
`

const FilterControl = styled.div`
  align-items: center;
  background: var(--surface-hover, #f7f9fc);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 11px;
  display: inline-flex;
  gap: 2px;
  padding: 3px;
`

const FilterButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  border: 0;
  border-radius: 9px;
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(16, 20, 31, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? 'var(--ink-strong, #10141f)' : 'var(--text-muted, #6f7686)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1;
  padding: 7px 12px;
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'var(--surface-pressed, #eef1f7)'};
    color: var(--ink-strong, #10141f);
  }

  ${focusRing}
`

const ListSurface = styled.div`
  background: var(--surface, #ffffff);
  border: 1px solid var(--surface-pressed, #edf1f7);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Row = styled.div`
  align-items: center;
  border-top: 1px solid var(--border-soft, #eceff3);
  cursor: pointer;
  display: grid;
  gap: 16px;
  grid-template-columns: 44px minmax(0, 1fr) auto auto;
  min-height: 68px;
  min-width: 0;
  padding: 12px 16px;
  transition: background-color 0.16s ease;

  &:first-child {
    border-top: 0;
  }

  &:hover,
  &:focus-within {
    background: var(--surface-hover, #f5f7fb);
  }

  &:active {
    background: var(--surface-pressed, #eef1f7);
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

const RowIcon = styled.div`
  align-items: center;
  display: flex;
  height: 44px;
  justify-content: center;
  width: 44px;
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const RowName = styled.div`
  align-items: center;
  color: var(--ink-strong, #121722);
  display: flex;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;
  min-width: 0;

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const RowWays = styled.div<{ $muted?: boolean }>`
  color: ${({ $muted }) => $muted ? 'var(--text-faint, #8a919e)' : 'var(--text-soft, #7d8492)'};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowSide = styled.div`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  max-width: 320px;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 900px) {
    display: none;
  }
`

const RowActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const SkeletonRow = styled.div`
  align-items: center;
  border-top: 1px solid var(--border-soft, #eceff3);
  display: grid;
  gap: 16px;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  min-height: 68px;
  padding: 12px 16px;

  &:first-child {
    border-top: 0;
  }
`

// Compact buttons for list rows: the app's buttons are 42px tall, too much for a row.
const actionButtonStyle = { minHeight: 34, padding: '0 14px', fontSize: 13 }
const skeletonCircle = { borderRadius: 999, height: 40, width: 40 }
const skeletonLine = (width: number, height = 12): React.CSSProperties => ({ borderRadius: 6, height, width })

interface ContactAccounts {
  customerId: string
  name: string
  email: string
  accounts: ExternalAccount[]
}

interface RecentRecipient {
  customerId: string
  name: string
  at: number
}

// Send delivers to wallets and EUR IBANs today. Other account types stay
// listed so the record is complete, without a shortcut that would dead-end.
const canSendTo = (account: ExternalAccount): boolean =>
  isCryptoExternalAccount(account) || isEurIbanExternalAccount(account)

const accountLabel = (account: ExternalAccount): string => {
  if (account.crypto) return `${networkName(account.crypto.network)} wallet`
  if (account.iban) return `${(account.currency || 'EUR').toUpperCase()} account ··${account.iban.last4}`
  if (account.account) return `USD account ··${account.account.last4}`
  if (account.gbAccount) return `GBP account ··${account.gbAccount.last4}`
  return `${(account.currency || '').toUpperCase()} ${String(account.accountType)} account`.trim()
}

const waysSummary = (accounts: ExternalAccount[]): string => {
  const labels = accounts.map(accountLabel)
  if (labels.length <= 2) return labels.join(' · ')
  return `${labels.slice(0, 2).join(' · ')} +${labels.length - 2}`
}

const groupByContact = (rows: ExternalAccountWithContact[]): Map<string, ContactAccounts> => {
  const byContact = new Map<string, ContactAccounts>()
  for (const row of rows) {
    const account = row.account
    const entry = byContact.get(account.customerId) ?? {
      customerId: account.customerId,
      name: row.customerName?.trim() || account.accountOwnerName,
      email: row.customerEmail?.trim() ?? '',
      accounts: []
    }
    entry.accounts.push(account)
    byContact.set(account.customerId, entry)
  }
  return byContact
}

const contactDisplayName = (contact: { name?: string, email?: string }): string =>
  contact.name?.trim() || contact.email?.trim() || 'Contact'

const ContactsDirectory = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const canReadContacts = has('ReadCustomers')
  const canReadContactAccounts = has('ReadExternalAccount')
  const canReadPayouts = has('ReadPayouts')

  const [contacts, setContacts] = useState<Customer[]>([])
  const [totalContacts, setTotalContacts] = useState(0)
  const [page, setPage] = useState(0)
  const [hasLoadedContacts, setHasLoadedContacts] = useState(false)
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ContactFilter>('all')

  const [accountsByContact, setAccountsByContact] = useState<Map<string, ContactAccounts>>(new Map())
  const [accountsNotice, setAccountsNotice] = useState('')
  const [orgWideAccounts, setOrgWideAccounts] = useState<boolean | undefined>(undefined)

  const [recentPayouts, setRecentPayouts] = useState<Payout[]>([])
  const canReadOwnAccounts = has('ReadWithdrawalAccount')
  const [ownAccounts, setOwnAccounts] = useState<WithdrawalAccount[] | undefined>(undefined)
  const [hasLoadedRecents, setHasLoadedRecents] = useState(false)

  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false)
  const [accountTarget, setAccountTarget] = useState<{ id: string, name: string } | null>(null)

  const networks = testMode ? testNetworks : prodNetworks

  // Typing narrows the page immediately; the server is asked once typing pauses.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(query.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(handle)
  }, [query])

  const loadContacts = async (): Promise<void> => {
    if (!canReadContacts) return
    setIsLoadingContacts(true)
    try {
      const result = await Radom.listCustomers(
        page * PAGE_SIZE,
        PAGE_SIZE,
        networks,
        filter === 'payers' ? 'totalSpendDescending' : 'nameAscending',
        { search: search || undefined, minimumTotalSpend: filter === 'payers' ? '0.000001' : undefined }
      )
      setContacts(result.data)
      setTotalContacts(result.total)
    } catch (err) {
      console.error('Failed to load contacts', err)
      setContacts([])
      setTotalContacts(0)
    }
    setIsLoadingContacts(false)
    setHasLoadedContacts(true)
  }

  const loadContactAccounts = async (): Promise<void> => {
    if (!canReadContactAccounts) return
    setAccountsNotice('')
    try {
      const { data } = await Radom.listOrganizationExternalAccounts()
      setAccountsByContact(groupByContact(data))
      setOrgWideAccounts(true)
    } catch (err) {
      console.error('Failed to load recipient accounts', err)
      setOrgWideAccounts(false)
    }
  }

  // Without the org-wide list, look up accounts for the contacts on screen,
  // one request each, and remember what was found.
  const loadAccountsForPage = async (pageContacts: Customer[]): Promise<void> => {
    if (!canReadContactAccounts || pageContacts.length === 0) return
    const targets = pageContacts.slice(0, FALLBACK_ACCOUNT_LOOKUPS)
    const results = await Promise.allSettled(targets.map(async c => await Radom.listExternalAccounts(c.id)))
    setAccountsByContact(prev => {
      const next = new Map(prev)
      results.forEach((result, i) => {
        if (result.status !== 'fulfilled') return
        const contact = targets[i]
        next.set(contact.id, {
          customerId: contact.id,
          name: contactDisplayName(contact),
          email: contact.email ?? '',
          accounts: result.value
        })
      })
      return next
    })
    setAccountsNotice('Ways to pay are loaded per contact on this page until the recipients list is enabled.')
  }

  const loadRecents = async (): Promise<void> => {
    if (!canReadPayouts) {
      setHasLoadedRecents(true)
      return
    }
    try {
      const result = await Radom.listPayouts(0, 12, testMode, [])
      setRecentPayouts(result.data)
    } catch (err) {
      console.error('Failed to load recent payouts', err)
      setRecentPayouts([])
    }
    setHasLoadedRecents(true)
  }

  useEffect(() => {
    if (!permissionsLoaded) return
    void loadContactAccounts()
    void loadRecents()
    if (canReadOwnAccounts) {
      Radom.listWithdrawalAccounts()
        .then(accounts => setOwnAccounts(accounts.filter(account => {
          if (!account.accountDetails.Crypto) return !testMode
          const method = getMethod(account.accountDetails.Crypto.network, account.accountDetails.Crypto.token)
          return testMode ? !!method.isTestnet : !method.isTestnet
        })))
        .catch(err => {
          console.error('Failed to load your accounts', err)
          setOwnAccounts([])
        })
    }
  }, [permissionsLoaded, testMode])

  useEffect(() => {
    if (!permissionsLoaded) return
    void loadContacts()
  }, [permissionsLoaded, testMode, page, search, filter])

  useEffect(() => {
    if (orgWideAccounts === false) void loadAccountsForPage(contacts)
  }, [orgWideAccounts, contacts])

  const term = query.trim().toLowerCase()
  const matches = (...parts: Array<string | undefined>): boolean =>
    !term || parts.some(part => (part ?? '').toLowerCase().includes(term))

  const accountsSearchText = (entry: ContactAccounts | undefined): string =>
    (entry?.accounts ?? []).map(a => `${accountLabel(a)} ${a.accountOwnerName} ${a.crypto?.address ?? ''}`).join(' ')

  // "Can be paid" spans every contact with a payable account, not just the
  // current page, so it is served from the org-wide account map.
  const payableEntries = useMemo((): ContactAccounts[] =>
    [...accountsByContact.values()]
      .filter(entry => entry.accounts.some(canSendTo))
      .sort((a, b) => a.name.localeCompare(b.name)),
  [accountsByContact])

  const visibleContacts = contacts.filter(contact =>
    matches(contact.name, contact.email, accountsSearchText(accountsByContact.get(contact.id))))
  const visiblePayable = payableEntries.filter(entry =>
    matches(entry.name, entry.email, accountsSearchText(entry)))

  // Recent recipients, newest first, one tile per contact.
  const recents = useMemo((): RecentRecipient[] => {
    const seen = new Set<string>()
    const tiles: RecentRecipient[] = []
    const sorted = [...recentPayouts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    for (const payout of sorted) {
      const customerIds = [
        ...(payout.cryptoEntries ?? []).map(entry => entry.customerId),
        ...(payout.fiatEntries ?? []).map(entry => entry.customerId)
      ].filter((id): id is string => !!id)
      for (const customerId of customerIds) {
        if (seen.has(customerId)) continue
        const known = accountsByContact.get(customerId) ?? contacts.find(c => c.id === customerId)
        if (!known) continue
        seen.add(customerId)
        tiles.push({ customerId, name: contactDisplayName(known), at: new Date(payout.createdAt).getTime() })
        if (tiles.length >= RECENT_LIMIT) return tiles
      }
    }
    return tiles
  }, [recentPayouts, accountsByContact, contacts])

  const orgName = User.organization?.name?.trim() || 'Your organization'

  const formatSpend = (value: string): string => {
    const amount = Number(value)
    if (!(amount > 0)) return ''
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: User.organization?.defaultCurrency || 'USD'
    }).format(amount)
  }

  const refreshAccounts = (): void => {
    if (orgWideAccounts === false) {
      void loadAccountsForPage(contacts)
    } else {
      void loadContactAccounts()
    }
  }

  const renderContactRow = (
    id: string,
    name: string,
    email: string,
    accounts: ExternalAccount[],
    extras: { spend?: string, verified?: boolean }
  ): ReactElement => {
    const sendable = accounts.filter(canSendTo)
    const open = (): void => navigate(`/customer/${id}`)
    const side = [email, extras.spend ? `Paid you ${extras.spend}` : ''].filter(Boolean).join(' · ')
    return <Row
      key={id}
      role='link'
      tabIndex={0}
      aria-label={`${name}, open contact`}
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter') open() }}>
      <RowIcon><RecipientAvatar name={name} size={40} /></RowIcon>
      <RowText>
        <RowName>
          <span>{name}</span>
          {extras.verified && <StatusBadge status='active'>KYC Verified</StatusBadge>}
        </RowName>
        <RowWays $muted={accounts.length === 0}>
          {accounts.length > 0 ? waysSummary(accounts) : 'No way to pay yet'}
        </RowWays>
      </RowText>
      <RowSide title={side}>{side}</RowSide>
      <RowActions onClick={e => e.stopPropagation()}>
        {sendable.length > 0
          ? <Gate permission='WritePayouts'>
            <PrimaryButton
              style={actionButtonStyle}
              onClick={() => navigate(`/send?customer=${id}${sendable.length === 1 ? `&account=${sendable[0].id}` : ''}`)}>
              Send funds
            </PrimaryButton>
          </Gate>
          : <Gate permission='WriteExternalAccount'>
            <SecondaryButton style={actionButtonStyle} onClick={() => setAccountTarget({ id, name })}>
              Add account
            </SecondaryButton>
          </Gate>}
      </RowActions>
    </Row>
  }

  const renderSkeletonRows = (count: number): ReactElement =>
    <ListSurface aria-hidden='true'>
      {Array.from({ length: count }, (_, i) =>
        <SkeletonRow key={i}>
          <LoadingBar style={skeletonCircle} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <LoadingBar style={skeletonLine(160, 14)} />
            <LoadingBar style={skeletonLine(220)} />
          </div>
          <LoadingBar style={skeletonLine(140)} />
        </SkeletonRow>
      )}
    </ListSurface>

  const showingPayable = filter === 'payable'
  const showListSkeleton = !hasLoadedContacts || (isLoadingContacts && contacts.length === 0)
  const listIsEmpty = showingPayable ? visiblePayable.length === 0 : visibleContacts.length === 0
  const emptyText = term
    ? 'No contacts match your search.'
    : showingPayable
      ? 'No contact has a saved bank account or wallet yet.'
      : filter === 'payers'
        ? 'No contact has paid you yet.'
        : 'No contacts yet. Add one, or paste an IBAN or wallet address in Send funds.'
  const showRecents = !term && canReadPayouts && (!hasLoadedRecents || recents.length > 0)

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Contacts' />
        <PageActionRow>
          <Gate permission='WriteCustomers'>
            <PrimaryButton onClick={() => setIsCreateContactOpen(true)}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Add contact</span>
            </PrimaryButton>
          </Gate>
        </PageActionRow>
      </PageHeaderRow>

      <SearchBar>
        <SearchGlyph viewBox='0 0 20 20' fill='none' aria-hidden='true'>
          <circle cx='9' cy='9' r='6' stroke='currentColor' strokeWidth='1.8' />
          <path d='M13.5 13.5 17 17' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
        </SearchGlyph>
        <input
          type='search'
          placeholder='Search by name, email, account digits or wallet address'
          value={query}
          onChange={e => setQuery(e.target.value)} />
      </SearchBar>

      {showRecents &&
        <Section>
          <SectionHeader><SectionLabel>Recent</SectionLabel></SectionHeader>
          {!hasLoadedRecents
            ? <RecentSkeleton aria-hidden='true'>
              {Array.from({ length: 5 }, (_, i) =>
                <div key={i}>
                  <LoadingBar style={{ borderRadius: 999, height: 52, width: 52 }} />
                  <LoadingBar style={skeletonLine(64, 10)} />
                </div>
              )}
            </RecentSkeleton>
            : <RecentStrip>
              {recents.map(recent =>
                <RecentPerson
                  key={recent.customerId}
                  type='button'
                  title={`Send funds to ${recent.name}`}
                  onClick={() => navigate(`/send?customer=${recent.customerId}`)}>
                  <RecipientAvatar name={recent.name} size={52} />
                  <span>{recent.name}</span>
                </RecentPerson>
              )}
            </RecentStrip>}
        </Section>}

      {!term && canReadOwnAccounts &&
        <Section>
          <SectionHeader><SectionLabel>You</SectionLabel></SectionHeader>
          <ListSurface>
            <Row
              role='link'
              tabIndex={0}
              aria-label='Your own accounts'
              onClick={() => navigate('/accounts')}
              onKeyDown={e => { if (e.key === 'Enter') navigate('/accounts') }}>
              <RowIcon><RecipientAvatar name={orgName} size={40} /></RowIcon>
              <RowText>
                <RowName><span>{orgName}</span><StatusBadge status='active'>You</StatusBadge></RowName>
                <RowWays $muted={!ownAccounts || ownAccounts.length === 0}>
                  {ownAccounts === undefined
                    ? 'Loading your accounts'
                    : ownAccounts.length === 0
                      ? 'No account of your own saved yet'
                      : `${ownAccounts.length} of your own wallets and bank accounts`}
                </RowWays>
              </RowText>
              <RowSide>Your own accounts</RowSide>
              <RowActions onClick={e => e.stopPropagation()}>
                <Gate permission='WriteWithdrawal'>
                  <PrimaryButton style={actionButtonStyle} onClick={() => navigate('/send')}>Send to yourself</PrimaryButton>
                </Gate>
              </RowActions>
            </Row>
          </ListSurface>
        </Section>}

      <Gate permission='ReadCustomers'>
        <Section>
          <SectionHeader>
            <SectionLabel>People and businesses</SectionLabel>
            <FilterControl aria-label='Contact filter'>
              {([
                ['all', 'All'],
                ['payable', 'Can be paid'],
                ['payers', 'Paid you']
              ] as Array<[ContactFilter, string]>).map(([value, label]) =>
                <FilterButton
                  key={value}
                  type='button'
                  $active={filter === value}
                  onClick={() => { setFilter(value); setPage(0) }}>
                  {label}
                </FilterButton>
              )}
            </FilterControl>
          </SectionHeader>
          {accountsNotice && <InlineNotice style={{ padding: '0 4px' }}>{accountsNotice}</InlineNotice>}
          {showListSkeleton && !showingPayable
            ? renderSkeletonRows(4)
            : listIsEmpty
              ? <EmptyState>{emptyText}</EmptyState>
              : <ListSurface style={{ opacity: isLoadingContacts ? 0.6 : 1, transition: 'opacity 0.16s ease' }}>
                {showingPayable
                  ? visiblePayable.map(entry =>
                    renderContactRow(entry.customerId, entry.name, entry.email, entry.accounts, {}))
                  : visibleContacts.map(contact => renderContactRow(
                    contact.id,
                    contactDisplayName(contact),
                    contact.email ?? '',
                    accountsByContact.get(contact.id)?.accounts ?? [],
                    {
                      spend: formatSpend(contact.totalSpend),
                      verified: (contact.tags ?? []).includes('identity_verified')
                    }
                  ))}
              </ListSurface>}
          {!showingPayable && hasLoadedContacts && totalContacts > PAGE_SIZE &&
            <Pagination
              curPage={page}
              numPages={Math.ceil(totalContacts / PAGE_SIZE)}
              onPageClick={setPage} />}
        </Section>
      </Gate>
    </DashboardPage>

    <Modal
      presentation='drawer'
      visible={isCreateContactOpen}
      onClose={() => setIsCreateContactOpen(false)}
      title='Add contact'>
      <div style={{ overflow: 'auto' }}>
        <CustomerForm onCreateCustomer={created => {
          setIsCreateContactOpen(false)
          void loadContacts()
          // A contact you add by hand is usually someone you want to pay, so
          // go straight to adding a way to pay them.
          if (has('WriteExternalAccount')) {
            setAccountTarget({ id: created.id, name: contactDisplayName(created) })
          }
        }} />
      </div>
    </Modal>

    {accountTarget &&
      <CreateExternalAccountModal
        presentation='drawer'
        customerId={accountTarget.id}
        contact={accountTarget}
        title={`Add a way to pay ${accountTarget.name}`}
        visible={!!accountTarget}
        onClose={() => setAccountTarget(null)}
        onAccountCreated={() => {
          setAccountTarget(null)
          refreshAccounts()
        }} />}
  </Container>
})

export default ContactsDirectory
